/**
 * ComprasAClassificar - Compras vinculadas ao projeto que ainda não têm lugar na ficha
 * O usuário decide: vincular a um item do checklist ou manter como compra extra
 */
import React, { useState } from 'react';
import api from '../../lib/axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { PackageSearch, Link2, Archive, Loader2 } from 'lucide-react';
import { formatCurrency } from './constantes';

const ComprasAClassificar = ({ project, embarcacaoIndex, onRefresh }) => {
  const [entradaVincular, setEntradaVincular] = useState(null);
  const [checklistIndex, setChecklistIndex] = useState('');
  const [saving, setSaving] = useState(false);

  const entradas = project?.compras_a_classificar || [];
  const pendentes = entradas.filter((e) => e.status === 'pendente');
  const extras = entradas.filter((e) => e.status === 'extra');
  const totalExtras = extras.reduce((s, e) => s + (e.valor_total || 0), 0);

  if (pendentes.length === 0 && extras.length === 0) return null;

  const checklistServidor =
    project?.embarcacoes?.[embarcacaoIndex]?.checklist_preenchido || [];

  const classificar = async (entrada, payload, msgOk) => {
    setSaving(true);
    try {
      await api.post(`/projects/${project.id}/compras-classificar/${entrada.id}`, payload);
      toast.success(msgOk);
      setEntradaVincular(null);
      setChecklistIndex('');
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao classificar compra');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mb-4 border-amber-300 bg-amber-50/50" data-testid="compras-a-classificar-card">
      <CardContent className="pt-4 pb-4">
        <div className="flex items-center gap-2 mb-3">
          <PackageSearch className="h-4 w-4 text-amber-700" />
          <h3 className="text-sm font-semibold text-amber-900">
            Compras a Classificar
            {pendentes.length > 0 && (
              <Badge className="ml-2 bg-amber-200 text-amber-900 hover:bg-amber-200">{pendentes.length}</Badge>
            )}
          </h3>
        </div>

        {pendentes.length > 0 && (
          <div className="space-y-2">
            {pendentes.map((e) => (
              <div key={e.id} className="flex items-center gap-3 flex-wrap p-2.5 bg-white rounded-lg border border-amber-200 text-sm" data-testid={`compra-classificar-${e.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{e.produto_nome}</p>
                  <p className="text-xs text-gray-500">
                    {e.quantidade} × {formatCurrency(e.valor_unitario)} = <strong>{formatCurrency(e.valor_total)}</strong>
                    {' · '}via {e.pedido_numero}{e.fornecedor_nome ? ` · ${e.fornecedor_nome}` : ''}
                  </p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    onClick={() => { setEntradaVincular(e); setChecklistIndex(''); }}
                    data-testid={`btn-vincular-compra-${e.id}`}>
                    <Link2 className="h-3 w-3 mr-1" /> Vincular à ficha
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-gray-600"
                    onClick={() => classificar(e, { acao: 'extra' }, 'Compra mantida como extra do projeto')}
                    disabled={saving}
                    data-testid={`btn-extra-compra-${e.id}`}>
                    <Archive className="h-3 w-3 mr-1" /> Manter como extra
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {extras.length > 0 && (
          <p className="text-xs text-amber-800 mt-3" data-testid="compras-extras-resumo">
            <Archive className="h-3 w-3 inline mr-1" />
            {extras.length} compra(s) mantida(s) como extra — total {formatCurrency(totalExtras)}
          </p>
        )}

        <Dialog open={!!entradaVincular} onOpenChange={(o) => !o && setEntradaVincular(null)}>
          <DialogContent className="max-w-md" data-testid="dialog-vincular-compra">
            <DialogHeader>
              <DialogTitle>Vincular à Ficha Técnica</DialogTitle>
              <DialogDescription>
                Escolha em qual item do checklist a compra <strong>{entradaVincular?.produto_nome}</strong> deve entrar.
                Ela nascerá com o custo real ({formatCurrency(entradaVincular?.valor_total)}).
              </DialogDescription>
            </DialogHeader>
            <Select value={checklistIndex} onValueChange={setChecklistIndex}>
              <SelectTrigger data-testid="select-checklist-vincular">
                <SelectValue placeholder="Selecione o item do checklist" />
              </SelectTrigger>
              <SelectContent>
                {checklistServidor.map((chk, idx) => (
                  <SelectItem key={idx} value={String(idx)}>
                    {(chk.categoria || 'Sem categoria') + ' › ' + (chk.descricao || chk.item || `Item ${idx + 1}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEntradaVincular(null)}>Cancelar</Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={checklistIndex === '' || saving}
                onClick={() => classificar(entradaVincular, {
                  acao: 'vincular',
                  embarcacao_index: embarcacaoIndex,
                  checklist_index: parseInt(checklistIndex),
                }, 'Compra vinculada à ficha técnica!')}
                data-testid="btn-confirmar-vincular"
              >
                {saving && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
                Vincular
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default ComprasAClassificar;
